import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usersApi } from '@/services/api';
import { 
  ChevronDownIcon,
  UserIcon,
  BuildingStorefrontIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  StarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface RoleOption {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  route: string;
  description: string;
}

const RoleSwitcherDropdown: React.FC = () => {
  const { user, isSuperAdmin, isAdmin, isStaff, isUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState<string>('');
  const [sellerEligibility, setSellerEligibility] = useState<{ can_be_seller: boolean; has_shop: boolean } | null>(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const hasCheckedEligibility = useRef(false);
  const isCheckingRef = useRef(false);

  // Reset eligibility check when user changes
  useEffect(() => {
    hasCheckedEligibility.current = false;
    isCheckingRef.current = false;
    setSellerEligibility(null);
  }, [user?.id]);

  // Check seller eligibility - only once per user session
  useEffect(() => {
    // Prevent infinite loop: only check if user is authenticated, hasn't been checked, and isn't currently checking
    if (!user || hasCheckedEligibility.current || isCheckingRef.current) {
      return;
    }

    // Only check for users, admins, or super admins
    const userRole = user.role_id;
    const shouldCheck = userRole === 4 || userRole === 2 || userRole === 1; // user, admin, super_admin
    
    if (!shouldCheck) {
      hasCheckedEligibility.current = true;
      return;
    }

    const checkSellerEligibility = async () => {
      if (isCheckingRef.current) return; // Prevent concurrent calls
      
      isCheckingRef.current = true;
      try {
        setLoadingEligibility(true);
        const response = await usersApi.checkSellerEligibility();
        // Backend returns is_eligible, frontend expects can_be_seller
        const data = response.data;
        setSellerEligibility({
          can_be_seller: data.is_eligible || false,
          has_shop: data.has_shop || false
        });
        hasCheckedEligibility.current = true;
      } catch (error: any) {
        // Only log error once, don't spam console
        if (!hasCheckedEligibility.current) {
          console.error('Failed to check seller eligibility:', error);
        }
        // Set default values to prevent future checks
        setSellerEligibility({ can_be_seller: false, has_shop: false });
        hasCheckedEligibility.current = true;
      } finally {
        setLoadingEligibility(false);
        isCheckingRef.current = false;
      }
    };
    
    checkSellerEligibility();
  }, [user?.id]); // Only depend on user ID, not functions

  // Define available roles based on user's actual role
  const getAvailableRoles = (): RoleOption[] => {
    const roles: RoleOption[] = [];

    if (isSuperAdmin()) {
      // Super Admin can access all roles
      roles.push(
        { id: 'super_admin', name: 'Super Admin', icon: StarIcon, route: '/dashboard?role=super_admin', description: 'Full system access' },
        { id: 'admin', name: 'Admin', icon: ShieldCheckIcon, route: '/dashboard?role=admin', description: 'Admin panel access' },
        { id: 'staff', name: 'Staff', icon: UserGroupIcon, route: '/dashboard?role=staff', description: 'Content moderation' },
        { id: 'seller', name: 'Seller', icon: BuildingStorefrontIcon, route: '/dashboard?role=seller', description: 'Sell products' },
        { id: 'buyer', name: 'Buyer', icon: UserIcon, route: '/dashboard?role=buyer', description: 'Buy products' }
      );
    } else if (isAdmin()) {
      // Admin can access admin, staff, seller, and buyer
      roles.push(
        { id: 'admin', name: 'Admin', icon: ShieldCheckIcon, route: '/dashboard?role=admin', description: 'Admin panel access' },
        { id: 'staff', name: 'Staff', icon: UserGroupIcon, route: '/dashboard?role=staff', description: 'Content moderation' },
        { id: 'seller', name: 'Seller', icon: BuildingStorefrontIcon, route: '/dashboard?role=seller', description: 'Sell products' },
        { id: 'buyer', name: 'Buyer', icon: UserIcon, route: '/dashboard?role=buyer', description: 'Buy products' }
      );
    } else if (isStaff()) {
      // Staff can access staff, seller, and buyer
      roles.push(
        { id: 'staff', name: 'Staff', icon: UserGroupIcon, route: '/dashboard?role=staff', description: 'Content moderation' },
        { id: 'seller', name: 'Seller', icon: BuildingStorefrontIcon, route: '/dashboard?role=seller', description: 'Sell products' },
        { id: 'buyer', name: 'Buyer', icon: UserIcon, route: '/dashboard?role=buyer', description: 'Buy products' }
      );
    } else if (isUser()) {
      // Regular users can always switch between buyer and seller
      // Seller option is always available - if no shop exists, they'll be redirected to create one
      // Always include both buyer and seller options for regular users
      roles.push(
        { id: 'buyer', name: 'Buyer', icon: UserIcon, route: '/dashboard?role=buyer', description: 'Buy products' }
      );
      
      // Always add seller option for regular users (they can create shop if needed)
      roles.push(
        { id: 'seller', name: 'Seller', icon: BuildingStorefrontIcon, route: '/dashboard?role=seller', description: 'Sell products' }
      );
    }

    return roles;
  };

  const availableRoles = getAvailableRoles();
  
  // Get current role from localStorage, URL query parameter, or infer from path
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let roleFromUrl = params.get('role');
    
    // If no role in query params, check localStorage
    if (!roleFromUrl) {
      const savedRole = localStorage.getItem('selectedRole');
      if (savedRole && availableRoles.some(role => role.id === savedRole)) {
        roleFromUrl = savedRole;
      }
    }
    
    // If still no role, infer from URL path
    if (!roleFromUrl) {
      if (location.pathname.startsWith('/dashboard/super-admin')) {
        roleFromUrl = 'super_admin';
      } else if (location.pathname.startsWith('/dashboard/admin')) {
        roleFromUrl = 'admin';
      } else if (location.pathname.startsWith('/dashboard/staff')) {
        roleFromUrl = 'staff';
      } else if (location.pathname.startsWith('/dashboard/seller')) {
        roleFromUrl = 'seller';
      } else if (location.pathname.startsWith('/dashboard/buyer')) {
        roleFromUrl = 'buyer';
      }
    }
    
    setCurrentRoleId(roleFromUrl || '');
  }, [location.search, location.pathname, availableRoles]);
  
  // Get current role from URL or default to first available role
  const currentRole = availableRoles.find(role => role.id === currentRoleId) || availableRoles[0];

  const handleRoleChange = (role: RoleOption) => {
    setIsOpen(false);
    
    // Save selected role to localStorage for persistence
    localStorage.setItem('selectedRole', role.id);
    
    // If switching to seller and user doesn't have a shop, redirect to create shop
    if (role.id === 'seller' && isUser() && sellerEligibility && !sellerEligibility.has_shop) {
      // Navigate to shop creation page
      navigate('/dashboard/seller/shop/create');
      return;
    }
    
    // Otherwise, navigate to the role's dashboard
    navigate(role.route);
  };

  // Always show dropdown for regular users (buyer/seller switcher should always be visible)
  // For admin users, only show if they have multiple roles
  if (isUser()) {
    // Always show switcher for regular users - they should always see buyer/seller options
    // This ensures they can switch between buyer and seller modes
  } else if (availableRoles.length <= 1) {
    // For non-regular users, only show if they have multiple roles
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <currentRole.icon className="h-5 w-5" />
        <span>{currentRole.name}</span>
        <ChevronDownIcon className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-20">
            <div className="py-1">
              {availableRoles.map((role) => {
                const Icon = role.icon;
                const isSellerWithoutShop = role.id === 'seller' && isUser() && sellerEligibility && !sellerEligibility.can_be_seller;
                
                return (
                  <button
                    key={role.id}
                    onClick={() => handleRoleChange(role)}
                    className={`w-full flex items-start px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      isSellerWithoutShop ? 'opacity-60' : ''
                    }`}
                    disabled={loadingEligibility}
                  >
                    <Icon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{role.name}</p>
                        {isSellerWithoutShop && (
                          <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 ml-2" title="Create shop first" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {isSellerWithoutShop ? 'Create shop to enable' : role.description}
                      </p>
                    </div>
                  </button>
                );
              })}
              
              {/* Show message if user wants to be seller but doesn't have shop */}
              {isUser() && sellerEligibility && !sellerEligibility.can_be_seller && (
                <div className="px-4 py-2 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/dashboard/seller/shop/create');
                    }}
                    className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create Shop to Become Seller
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcherDropdown;

